import {
    createCompareInChart,
    getApiErrorMessage,
    IApiAxiosError,
    IScope,
} from "@/api";
import { Alert } from "@/components/shared/alerts/Alert";
import { MetadataChip } from "@/components/shared/chips/MetadataChip";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { trackComparisonCreated } from "@/lib/analytics";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { BookCheck } from "lucide-react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { MAX_SCOPES, ScopeSelectionList } from "./ScopeSelectionList";

interface IFormData {
    name: string;
    scope_ids: number[];
}

interface CreateChartModalProps {
    open: boolean;
    onClose: () => void;
    initialScopes: Pick<
        IScope,
        "id" | "clause_checklist_id" | "document_title"
    >[];
    clauseChecklistId: number;
    clauseChecklistName: string;
}

export const CreateChartModal = ({
    open,
    onClose,
    initialScopes,
    clauseChecklistId,
    clauseChecklistName,
}: CreateChartModalProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { organization } = useOrganization();

    const methods = useForm<IFormData>({
        defaultValues: {
            name: "",
            scope_ids: initialScopes.map((s) => s.id).slice(0, MAX_SCOPES),
        },
    });

    const { watch, handleSubmit } = methods;
    const scopeIds: number[] = watch("scope_ids");

    const {
        mutate,
        isPending,
        error,
        reset: resetMutation,
    } = useMutation({
        mutationFn: createCompareInChart,
        onSuccess: (data, variables) => {
            trackComparisonCreated({
                comparison_id: data.id,
                clause_checklist_id: data.clause_checklist_id,
                scope_count: variables.scope_ids.length,
            });
            onClose();
            navigate(`/organizations/comparisons/${data.id}`);
        },
    });

    const errorMessage = error
        ? getApiErrorMessage(error as IApiAxiosError, "Failed to create chart")
        : null;

    const onSubmit = (data: IFormData) => {
        mutate({
            name: data.name.trim() || "New Comparison",
            clause_checklist_id: clauseChecklistId,
            organization_id: organization.id,
            scope_ids: data.scope_ids,
        });
    };

    const handleClose = () => {
        if (!isPending) {
            resetMutation();
            onClose();
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Create Chart"
            subtitle="Compare clauses and fields across scopes"
            maxWidth="sm"
            isLoading={isPending}
            showCancel={!isPending}
            error={errorMessage}
            actions={
                !isPending
                    ? [
                          {
                              label: "Create Chart",
                              onClick: handleSubmit(onSubmit),
                              disabled: scopeIds.length < 2,
                              color: "primary",
                          },
                      ]
                    : []
            }
        >
            <FormProvider {...methods}>
                <Stack gap={2.5}>
                    <Alert severity="info" compact>
                        A chart compares clauses and fields across multiple
                        scopes, highlighting differences and similarities.
                    </Alert>

                    <Controller
                        name="name"
                        control={methods.control}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                {...field}
                                inputRef={ref}
                                label="Chart Name"
                                placeholder="New Comparison"
                                fullWidth
                            />
                        )}
                    />

                    <Box>
                        <Typography
                            variant="caption"
                            fontWeight={600}
                            color="text.secondary"
                            sx={{
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                mb: 0.75,
                                display: "block",
                            }}
                        >
                            Checklist
                        </Typography>
                        <MetadataChip
                            icon={<BookCheck />}
                            label={clauseChecklistName}
                            backgroundColor={theme.palette.successLight.main}
                            color={theme.palette.success.main}
                            hoverBackgroundColor={theme.palette.success.main}
                        />
                    </Box>

                    <Box>
                        <Typography
                            variant="caption"
                            fontWeight={600}
                            color="text.secondary"
                            sx={{
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                mb: 0.75,
                                display: "block",
                            }}
                        >
                            Select Scopes{" "}
                            <Typography
                                component="span"
                                variant="caption"
                                color="text.disabled"
                            >
                                ({scopeIds.length}/{MAX_SCOPES} selected)
                            </Typography>
                        </Typography>
                        <ScopeSelectionList
                            clauseChecklistId={clauseChecklistId}
                            organizationId={organization.id}
                            initialScopes={initialScopes}
                        />
                    </Box>

                    {scopeIds.length < 2 && (
                        <Alert severity="warning" compact>
                            Select between 2 and {MAX_SCOPES} scopes to create a
                            chart.
                        </Alert>
                    )}
                </Stack>
            </FormProvider>
        </Modal>
    );
};
